import { useState } from 'react';
import { Badge, Button, Center, Group, Paper, ScrollArea, Stack, Tabs, Text } from '@mantine/core';
import { IconClock, IconPhone } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { ErrorAlert } from '@/components/error-alert';
import { useSoftphone } from '@/hooks/use-softphone';
import { elapsedSeconds, formatClock, formatPhone } from '../format';
import { useNow } from '../use-now';
import { QueueCallersSubscription } from './graphql';
import type { WorkspaceQueue } from './types';

type Caller = {
  id: string;
  callerNumber: string;
  priority: number;
  enteredAt: string;
  sessionVariables: unknown;
};

function callerVariables(sessionVariables: unknown) {
  if (!sessionVariables || typeof sessionVariables !== 'object') return [];

  return Object.entries(sessionVariables as Record<string, unknown>).filter(([key]) => !key.startsWith('_'));
}

function CallerCard({ caller, now }: { caller: Caller; now: number }) {
  const displayVars = callerVariables(caller.sessionVariables);

  return (
    <Paper p="sm" withBorder>
      <Group justify="space-between" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} ff="monospace">
            {formatPhone(caller.callerNumber)}
          </Text>
          {displayVars.length > 0 && (
            <Group gap="xs" mt={2}>
              {displayVars.slice(0, 3).map(([key, value]) => (
                <Text key={key} size="xs" c="dimmed" truncate>
                  {String(value)}
                </Text>
              ))}
            </Group>
          )}
        </div>
        <Group gap="xs" wrap="nowrap">
          {caller.priority > 1 && (
            <Badge size="xs" color="red" variant="light">
              P{caller.priority}
            </Badge>
          )}
          <Group gap={4} wrap="nowrap">
            <IconClock size={10} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed" ff="monospace">
              {formatClock(elapsedSeconds(caller.enteredAt, now))}
            </Text>
          </Group>
        </Group>
      </Group>
    </Paper>
  );
}

function WaitingBanner({
  queue,
  organizationId,
  onError,
}: {
  queue: WorkspaceQueue;
  organizationId: string;
  onError: (message: string | null) => void;
}) {
  const { activeCall, isConnected } = useSoftphone();
  const [isClaiming, setIsClaiming] = useState(false);

  const takeNextCall = async () => {
    setIsClaiming(true);
    onError(null);

    const { error } = await api['call-control']['answer-queue-call'].post({ organizationId, queueId: queue.id });

    setIsClaiming(false);

    if (error) onError(getErrorMessage(error, 'Unable to claim the next call'));
  };

  return (
    <Paper
      p="md"
      radius="md"
      shadow="none"
      style={{
        background: 'linear-gradient(135deg, var(--mantine-color-green-9), var(--mantine-color-green-8))',
        border: 'none',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Text fw={600} c="white">
            {queue.waitingCount} caller{queue.waitingCount !== 1 ? 's' : ''} waiting
          </Text>
          <Group gap="xs">
            <IconClock size={12} color="rgba(255,255,255,0.7)" />
            <Text size="xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Longest wait: {formatClock(queue.longestWaitSecs)}
            </Text>
          </Group>
          {queue.ringingCount > 0 && (
            <Text size="xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {queue.ringingCount} offer{queue.ringingCount !== 1 ? 's' : ''} ringing agents
            </Text>
          )}
        </div>
        {queue.deliveryMode === 'pull' ? (
          <Button
            variant="white"
            leftSection={<IconPhone size={16} />}
            style={{ color: 'var(--mantine-color-green-8)' }}
            onClick={() => void takeNextCall()}
            loading={isClaiming}
            disabled={!isConnected || !!activeCall}
          >
            Take Next Call
          </Button>
        ) : (
          <Badge color="green" variant="white" size="lg">
            Auto-offer
          </Badge>
        )}
      </Group>
    </Paper>
  );
}

function RingingBanner({ ringingCount }: { ringingCount: number }) {
  return (
    <Paper
      p="md"
      radius="md"
      shadow="none"
      style={{
        background: 'linear-gradient(135deg, var(--mantine-color-blue-9), var(--mantine-color-blue-8))',
        border: 'none',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Text fw={600} c="white">
            Offer in progress
          </Text>
          <Text size="xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {ringingCount} active offer{ringingCount !== 1 ? 's' : ''}
          </Text>
        </div>
        <Badge color="blue" variant="white" size="lg">
          Live
        </Badge>
      </Group>
    </Paper>
  );
}

function QueueCallers({ queueId }: { queueId: string }) {
  const [{ data }] = useSubscription({ query: QueueCallersSubscription, variables: { queueId } });
  const now = useNow();

  if (!data || data.queueEntries.length === 0) return null;

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
        Waiting Callers
      </Text>
      {data.queueEntries.map((entry) => (
        <CallerCard key={entry.id} caller={entry} now={now} />
      ))}
    </Stack>
  );
}

export function QueuePanel({
  organizationId,
  queues,
  selectedQueueId,
  onQueueSelect,
}: {
  organizationId: string;
  queues: WorkspaceQueue[];
  selectedQueueId: string | null;
  onQueueSelect: (queueId: string) => void;
}) {
  const [claimError, setClaimError] = useState<string | null>(null);
  const selected = queues.find((q) => q.id === selectedQueueId);

  if (queues.length === 0) {
    return (
      <Center className="flex-1">
        <Text c="dimmed">No queues assigned</Text>
      </Center>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {queues.length > 1 && (
        <Tabs
          value={selectedQueueId}
          onChange={(value) => value && onQueueSelect(value)}
          variant="outline"
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
        >
          <Tabs.List px="md" pt="xs">
            {queues.map((queue) => (
              <Tabs.Tab
                key={queue.id}
                value={queue.id}
                rightSection={
                  queue.waitingCount > 0 ? (
                    <Badge size="xs" color="orange" variant="filled">
                      {queue.waitingCount}
                    </Badge>
                  ) : queue.ringingCount > 0 ? (
                    <Badge size="xs" color="blue" variant="light">
                      {queue.ringingCount}
                    </Badge>
                  ) : null
                }
              >
                {queue.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      )}

      <ScrollArea className="flex-1" p="md">
        {selected && (
          <Stack gap="md">
            <ErrorAlert message={claimError} />

            {selected.waitingCount > 0 && (
              <WaitingBanner queue={selected} organizationId={organizationId} onError={setClaimError} />
            )}

            {selected.waitingCount === 0 && selected.ringingCount > 0 && (
              <RingingBanner ringingCount={selected.ringingCount} />
            )}

            {selected.waitingCount === 0 && selected.ringingCount === 0 && (
              <Center py="3xl">
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={600} c="dimmed">
                    Queue Clear
                  </Text>
                  <Text size="sm" c="dimmed">
                    Waiting for incoming calls
                  </Text>
                </Stack>
              </Center>
            )}

            <QueueCallers queueId={selected.id} />
          </Stack>
        )}
      </ScrollArea>
    </div>
  );
}
